import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Ban, Check, Trash2 } from "lucide-react";

export default function WatchlistPage() {
  const { hasPerm } = useAuth();
  const [entries, setEntries] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [filter, setFilter] = useState("active");

  const load = async () => {
    try {
      const { data } = await api.get("/watchlist");
      setEntries(data);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/watchlist/${editing.id}`, {
          name: form.name, id_number: form.id_number,
          reason: form.reason, notes: form.notes,
        });
        toast.success("Entry updated");
      } else {
        await api.post("/watchlist", form);
        toast.success("Added to watch list");
      }
      setCreating(false); setEditing(null); setForm(BLANK); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ name: e.name, id_number: e.id_number || "", reason: e.reason, notes: e.notes || "" });
    setCreating(true);
  };

  const toggle = async (e) => {
    try {
      await api.patch(`/watchlist/${e.id}`, { active: !e.active });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.name} from the watch list?`)) return;
    try {
      await api.delete(`/watchlist/${e.id}`);
      toast.success("Removed"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? entries : entries.filter((e) => (filter === "active") ? e.active : !e.active);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="watchlist-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Entrance · Private list</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Watch list</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            People barred from entry. Names or ID numbers are matched at scan-time — a manager can still override with a written reason on the audit trail.
          </p>
        </div>
        {hasPerm("entrance.scan") && (
          <button onClick={() => { setEditing(null); setForm(BLANK); setCreating(true); }}
                  data-testid="watchlist-new-btn"
                  className="btn-pill btn-pill-solid h-10">
            <Plus size={14}/> Add to watch list
          </button>
        )}
      </div>

      <div className="mt-10 grid grid-cols-3 gap-x-8 gap-y-4 animate-fade-up animate-fade-up-1">
        <Stat num=".01" label="Total entries" value={entries.length}/>
        <Stat num=".02" label="Active" value={entries.filter((e) => e.active).length} warn/>
        <Stat num=".03" label="Deactivated" value={entries.filter((e) => !e.active).length}/>
      </div>

      <div className="mt-10 flex items-center gap-2 animate-fade-up animate-fade-up-2">
        {["active","inactive","all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
                  data-testid={`watchlist-filter-${f}`}
                  className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                    filter === f ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                  }`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-8 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-3" data-testid="watchlist-table">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">ID number</div>
          <div className="col-span-3">Reason</div>
          <div className="col-span-2">Notes</div>
          <div className="col-span-1 text-right">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {filtered.length === 0 && (
          <div className="py-8 ink-subtle text-center text-sm">No entries in this bucket.</div>
        )}
        {filtered.map((e) => (
          <div key={e.id} className="grid grid-cols-12 py-3 items-center" data-testid={`watchlist-row-${e.id}`}>
            <button onClick={() => openEdit(e)} className="col-span-3 pr-2 text-left min-w-0">
              <div className="flex items-center gap-2">
                <Ban size={12} className={e.active ? "text-red-500 shrink-0" : "text-foreground/30 shrink-0"}/>
                <span className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{e.name}</span>
              </div>
            </button>
            <div className="col-span-2 mono text-xs ink-subtle truncate">{e.id_number || "—"}</div>
            <div className="col-span-3 text-xs ink truncate pr-2">{e.reason}</div>
            <div className="col-span-2 text-xs ink-subtle italic truncate pr-2">{e.notes || "—"}</div>
            <div className="col-span-1 text-right">
              <span className={`text-[9px] uppercase tracking-[0.22em] font-bold ${e.active ? "text-red-500" : "text-foreground/40"}`}>
                {e.active ? "ACTIVE" : "OFF"}
              </span>
            </div>
            <div className="col-span-1 text-right flex items-center justify-end gap-1">
              <button onClick={() => toggle(e)} data-testid={`watchlist-toggle-${e.id}`}
                      className="tiny-label ink-subtle hover:ink text-[10px] px-2">
                {e.active ? "Off" : "On"}
              </button>
              <button onClick={() => remove(e)} data-testid={`watchlist-remove-${e.id}`}
                      className="ink-subtle hover:text-red-500 p-1">
                <Trash2 size={12}/>
              </button>
            </div>
          </div>
        ))}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="watchlist-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setCreating(false); setEditing(null); }}/>
          <form onSubmit={submit} className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Watch list</div>
                <h2 className="mt-2 display-heading text-2xl ink">{editing ? "Edit entry" : "Add to watch list"}</h2>
              </div>
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <F label="Full name"><input required autoFocus value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="watchlist-form-name"/></F>
            <F label="ID number (optional)"><input value={form.id_number} onChange={(e)=>setForm({...form, id_number: e.target.value})} className="i mono" data-testid="watchlist-form-id"/></F>
            <F label="Reason (required)"><input required value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} className="i" data-testid="watchlist-form-reason"/></F>
            <F label="Notes"><textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i-tall" data-testid="watchlist-form-notes"/></F>

            <div className="mt-6 flex gap-2">
              <button type="button" onClick={() => { setCreating(false); setEditing(null); }} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button type="submit" data-testid="watchlist-form-submit" className="btn-pill btn-pill-solid h-11 flex-1"><Check size={13}/> {editing ? "Save" : "Add"}</button>
            </div>

            <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }
                     .i-tall { margin-top: 6px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 10px 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const BLANK = { name: "", id_number: "", reason: "", notes: "" };

function Stat({ num, label, value, warn }) {
  return (
    <div data-testid={`watchlist-stat-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono text-2xl font-semibold ${warn && value > 0 ? "text-red-500" : "ink"}`}>{value}</div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
